import { useRef, useEffect, useCallback } from "react";
import { useFlushOnPageHide } from "@/hooks/use-flush-on-page-hide";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIP_DATA } from "@/queries/queries";

export interface CropCenterKeyframe {
  time: number;
  endTime: number;
  x: number;
  y: number;
  /**
   * Zoom multiplier applied to the 9:16 crop window.
   * 1 = default (snug crop, original behavior). 2 = 2x zoomed in, etc.
   * Optional for backwards compatibility with keyframes saved before this field existed.
   */
  zoom?: number;
}

export const DEFAULT_CROP_ZOOM = 1;
export const MIN_CROP_ZOOM = 1;
export const MAX_CROP_ZOOM = 3;

export const getCropZoom = (kf: Pick<CropCenterKeyframe, "zoom">): number =>
  typeof kf.zoom === "number" && kf.zoom > 0 ? kf.zoom : DEFAULT_CROP_ZOOM;

interface Options {
  clipId: string | null;
  keyframes: CropCenterKeyframe[] | null;
  /** The clip the CURRENT `keyframes` belong to — same stale-window tag as
   *  useAutoSaveClipData; see the rationale there. */
  loadedClipId: string | null;
  enabled: boolean;
  delay?: number;
  onSaved?: (clipId: string, data: { cropCenterKeyframes: CropCenterKeyframe[] | null }) => void;
}

type PendingWrite = {
  clipId: string;
  /** null clears the saved framing (the export then derives it again). */
  keyframes: CropCenterKeyframe[] | null;
  serialized: string;
};

/** Debounced persistence of user crop-keyframe overrides. Going back to
 *  `null` (computed defaults) clears what was saved, so the export (which
 *  prefers saved keyframes) matches the preview; a pending write that is
 *  undone before it fires is dropped. Pending writes are flushed (not
 *  dropped) on clip switch and unmount. */
export function useAutoSaveCropKeyframes({
  clipId,
  keyframes,
  loadedClipId,
  enabled,
  delay = 500,
  onSaved,
}: Options) {
  const [updateClipData, { loading: saving }] = useMutation(UPDATE_CLIP_DATA);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string>("");
  const awaitingLoadRef = useRef<boolean>(true);
  const currentClipIdRef = useRef<string | null>(null);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const performSave = useCallback(
    async (write: PendingWrite) => {
      // Recorded when the write is sent, not when it returns: an undo that
      // arrives while it is in flight must still be compared with it.
      const previous = lastSavedRef.current;
      if (currentClipIdRef.current === write.clipId) lastSavedRef.current = write.serialized;
      try {
        const payload = { cropCenterKeyframes: write.keyframes };
        await updateClipData({
          variables: { id: write.clipId, data: payload },
        });
        onSavedRef.current?.(write.clipId, payload);
      } catch (error) {
        if (currentClipIdRef.current === write.clipId && lastSavedRef.current === write.serialized) {
          lastSavedRef.current = previous;
        }
        console.error("Auto-save crop keyframes failed:", error);
      }
    },
    [updateClipData],
  );

  /** Fire the pending write NOW (fire-and-forget). Never drops it. */
  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) void performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  // Clip switch: flush the old clip's pending write, await the new load.
  useEffect(() => {
    if (clipId !== currentClipIdRef.current) {
      firePending();
      currentClipIdRef.current = clipId;
      awaitingLoadRef.current = true;
      lastSavedRef.current = "";
    }
  }, [clipId, firePending]);

  useEffect(() => {
    if (!enabled || !clipId) return;
    // Stale window: these keyframes belong to another clip.
    if (loadedClipId !== clipId) return;

    // `null` = no user overrides; nothing to persist, but this IS the load —
    // seed so the user's first real edit is treated as an edit.
    const serialized = keyframes ? JSON.stringify(keyframes) : "";
    if (awaitingLoadRef.current) {
      awaitingLoadRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) {
      // Back to what is saved (e.g. an undo): nothing to write, and a
      // pending write of the undone state must not fire.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingRef.current = null;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { clipId, keyframes, serialized };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) void performSave(pending);
    }, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [keyframes, loadedClipId, clipId, enabled, delay, performSave]);

  // Unmount: flush instead of dropping.
  useEffect(() => () => firePendingRef.current(), []);

  return { saving };
}
